import React, { useEffect, useRef, useState } from "react";
import { baseUrl } from '../../config.js'
import useToken from "../../useToken.js";
import './Mensajes.css';
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const formatTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
};

const Mensajes = ({ tipo }) => {
    const { token } = useToken();
    const [publicaciones, setPublicaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const idSupport = params.idSupport || location.pathname.match(/mensajes\/([^/]+)/)?.[1];
    const [conversation, setConversation] = useState(null);
    const basePath = tipo === 'host' ? '/host/mensajes' : '/mensajes';

    const openConversation = (post) => {
        // Set first so the right panel updates even before the URL settles
        setConversation(post);
        if (String(post.idSupport) !== String(idSupport || '')) {
            navigate(`${basePath}/${post.idSupport}`);
        }
    };

    const updateConversationAsRead = (supportId) => {
        setPublicaciones(prev => prev.map(post => {
            if (String(post.idSupport) === String(supportId)) {
                return {
                    ...post,
                    lastMessage: post.lastMessage ? { ...post.lastMessage, isRead: 1 } : null,
                    isRead: 1
                };
            }
            return post;
        }));
    };

    useEffect(() => {
        if (!token) {
            setLoading(false);
            return;
        }
        getData();
    }, [token]);

    useEffect(() => {
        if (!idSupport) {
            setConversation(null);
            return;
        }
        if (publicaciones.length > 0) {
            const conv = publicaciones.find(p => String(p.idSupport) === String(idSupport));
            if (conv) setConversation(conv);
        }
    }, [idSupport, publicaciones]);

    const getData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${baseUrl}Messages/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            const sortedData = (Array.isArray(data) ? data : []).sort((a, b) => {
                const dateA = new Date(a.lastMessage?.timestamp || a.timestamp || 0);
                const dateB = new Date(b.lastMessage?.timestamp || b.timestamp || 0);
                return dateB - dateA;
            });
            setPublicaciones(sortedData);
            if (idSupport && sortedData.length > 0) {
                const conv = sortedData.find(p => String(p.idSupport) === String(idSupport));
                if (conv) setConversation(conv);
            }
        } catch (error) {
            console.error("Error fetching mensajes:", error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="msg">
            {loading &&
                <div className="msg-empty">
                    <div className="msg-empty__card">
                        <h2>Cargando mensajes…</h2>
                    </div>
                </div>
            }

            {!loading && publicaciones.length === 0 &&
                <div className="msg-empty">
                    <div className="msg-empty__card">
                        <span className="msg-empty__icon" aria-hidden="true">💬</span>
                        <h2>No tienes mensajes</h2>
                        <p>Cuando inicies una conversación, aparecerá aquí.</p>
                    </div>
                </div>
            }

            {!loading && publicaciones.length > 0 &&
                <>
                    <aside className="msg-side">
                        <div className="msg-side__head">
                            <h1>Mensajes</h1>
                            <span>{publicaciones.length}</span>
                        </div>
                        <div className="msg-side__list">
                            {publicaciones.map((post, index) => {
                                const active = String(post.idSupport) === String(conversation?.idSupport || idSupport);
                                const unread = !(post?.lastMessage?.isRead || post.isRead);
                                return (
                                    <button
                                        type="button"
                                        key={post.idSupport || index}
                                        className={"msg-thread" + (active ? " is-active" : "") + (unread ? " is-unread" : "")}
                                        onClick={() => openConversation(post)}
                                    >
                                        <div className="msg-thread__avatar">
                                            {post.imageUrl
                                                ? <img src={post.imageUrl} alt="" />
                                                : <span>{(post?.message || post?.object?.name || "A").charAt(0).toUpperCase()}</span>
                                            }
                                        </div>
                                        <div className="msg-thread__body">
                                            <div className="msg-thread__top">
                                                <strong>{post?.object?.name || "Asesoramiento"}</strong>
                                                <div className="msg-thread__meta">
                                                    {unread && <span className="msg-thread__badge">Nuevo</span>}
                                                    <time>{formatDate(post.lastMessage?.timestamp || post.timestamp)}</time>
                                                </div>
                                            </div>
                                            <p>{post?.lastMessage?.message || post.message || "Sin mensajes"}</p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </aside>

                    <section className="msg-main">
                        {conversation ? (
                            <>
                                <header className="msg-main__head">
                                    <div className="msg-main__avatar">
                                        {conversation.imageUrl
                                            ? <img src={conversation.imageUrl} alt="" />
                                            : <span>{(conversation?.message || "A").charAt(0).toUpperCase()}</span>
                                        }
                                    </div>
                                    <div className="msg-main__meta">
                                        <h2>{conversation?.object?.name || "Asesoramiento"}</h2>
                                        <p>{conversation?.message || "Conversación de soporte"}</p>
                                    </div>
                                    {conversation?.url &&
                                        <Link to={conversation.url} className="msg-main__link" title="Enlace relacionado">
                                            Ver detalle
                                        </Link>
                                    }
                                </header>
                                <Conversation
                                    id={conversation?.idSupport}
                                    updateConversationAsRead={updateConversationAsRead}
                                />
                            </>
                        ) : (
                            <div className="msg-empty msg-empty--panel">
                                <div className="msg-empty__card">
                                    <h2>Selecciona una conversación</h2>
                                    <p>Elige un chat de la lista para ver los mensajes.</p>
                                </div>
                            </div>
                        )}
                    </section>
                </>
            }
        </div>
    );
};

const RatingBlock = ({ idSupport, onRated }) => {
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const { token } = useToken();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!rating) return;
        try {
            const response = await fetch(`${baseUrl}api/chat/calificar/${idSupport}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ rating, comment: rating < 5 ? comment : "" })
            });
            const data = await response.json();
            if (data.success) {
                setSubmitted(true);
                if (onRated) onRated();
            }
        } catch (err) {
            alert('Error al enviar la calificación');
        }
    };

    if (submitted) return <div className="msg-rating"><h3>¡Gracias por tu opinión!</h3></div>;

    return (
        <form className="msg-rating" onSubmit={handleSubmit}>
            <h3>Califica tu experiencia</h3>
            <div className="msg-rating__stars">
                {[1, 2, 3, 4, 5].map(num => (
                    <button type="button" key={num} className={num <= rating ? "is-on" : ""} onClick={() => setRating(num)}>★</button>
                ))}
            </div>
            {rating > 0 && rating < 5 && (
                <textarea
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="¿Cómo podemos mejorar?"
                    rows={3}
                />
            )}
            <button type="submit" className="msg-send">Enviar</button>
        </form>
    );
}

const Conversation = ({ id, updateConversationAsRead }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const { token } = useToken();
    const [showRating, setShowRating] = useState(false);
    const listRef = useRef(null);

    useEffect(() => {
        getMessages();
        const timer = setTimeout(() => setShowRating(true), 10 * 60 * 1000);
        return () => clearTimeout(timer);
    }, [id]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages]);

    const getMessages = async () => {
        if (!id) return;
        try {
            const response = await fetch(`${baseUrl}Messages/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            setMessages(Array.isArray(data) ? data : []);
            await markMessagesAsRead();
        } catch (error) {
            console.error("Error fetching messages:", error);
            setMessages([]);
        }
    }

    const markMessagesAsRead = async () => {
        try {
            const response = await fetch(`${baseUrl}Messages/markAsRead/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) throw new Error('Error marking messages as read');
            if (updateConversationAsRead) updateConversationAsRead(id);
        } catch (error) {
            console.error("Error marking messages as read:", error);
        }
    }

    const sendMensaje = async () => {
        if (!newMessage.trim()) return;
        try {
            const response = await fetch(`${baseUrl}Messages/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    idSupport: id,
                    message: newMessage,
                })
            });
            if (!response.ok) throw new Error('Network response was not ok');
            setNewMessage("");
            getMessages();
        } catch (error) {
            console.error("Error sending message:", error);
        }
    }

    return (
        <div className="msg-chat">
            <div className="msg-chat__list" ref={listRef}>
                {messages.length === 0 &&
                    <div className="msg-chat__hint">Aún no hay mensajes en esta conversación.</div>
                }
                {messages.map((message, index) => {
                    const isBot = !message.sender || message.sender == "bot";
                    return (
                        <div key={index} className={"msg-bubble" + (isBot ? " is-in" : " is-out")}>
                            <div className="msg-bubble__content">
                                {message.title && <strong>{message.title}</strong>}
                                <pre>{message.message}</pre>
                                <time>{formatTime(message.timestamp) || formatDate(message.timestamp)}</time>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="msg-composer">
                <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMensaje();
                        }
                    }}
                    placeholder="Escribe tu mensaje..."
                    rows={1}
                />
                <button type="button" className="msg-send" onClick={sendMensaje} disabled={!newMessage.trim()}>
                    Enviar
                </button>
            </div>

            {showRating && <RatingBlock idSupport={id} />}
        </div>
    )
}

export default Mensajes;
